import { useEffect, useState } from "react";
import {
  ArticleActions,
  ArticlesForReviewRequest,
  ItemWithMetaData,
  ItemsForReviewResponse,
} from "../api/Items";
import { useFetchArticlesForReview } from "../api/ItemsApi";
const pageSize = 25;
export type WebContentHeap = {
  REVIEW?: ItemsForReviewResponse;
  ADDED?: ItemsForReviewResponse;
  REMOVED?: ItemsForReviewResponse;
};

function sortArticlesByDate(a: ItemWithMetaData, b: ItemWithMetaData) {
  return (
    new Date(b.item.content.created_at ?? "0").getTime() -
    new Date(a.item.content.created_at ?? "0").getTime()
  );
}

function sortArticlesByScoreAndThenDate(
  a: ItemWithMetaData,
  b: ItemWithMetaData
) {
  const scoreComparison =
    (b.item.content.score ?? 0) - (a.item.content.score ?? 0);
  if (scoreComparison !== 0) return scoreComparison;
  return sortArticlesByDate(a, b);
}

export type WebContentErrors = {
  REVIEW?: boolean;
  ADDED?: boolean;
  REMOVED?: boolean;
};

export function useWebContentActions(report_uuid: string) {
  const [articlesHeap, setArticlesHeap] = useState<WebContentHeap>({});
  const [reviewPageSize, setReviewPageSize] = useState(pageSize);
  const [isLoading, setIsLoading] = useState(true);
  const { mutateAsync: getArticlesAsync } = useFetchArticlesForReview();
  const [errorLoadingArticles, setErrorLoadingArticles] =
    useState<WebContentErrors>({});

  function manageError() {
    const emptyResponse: ItemsForReviewResponse = {
      items: [],
      total: 0,
      page: 0,
      size: 0,
    };
    return emptyResponse;
  }

  async function loadArticlesByAction(
    action: ArticleActions,
    page: number,
    newPageSize?: number
  ) {
    setIsLoading(true);
    setErrorLoadingArticles((p) => ({ ...p, [action]: false }));

    let articlesRequest: ArticlesForReviewRequest = {
      page_num: page,
      page_size: pageSize,
      article_action: action,
      report_id: report_uuid,
    };

    if (action === ArticleActions.REVIEW) {
      articlesRequest.page_size = newPageSize ?? reviewPageSize;
      return getArticlesAsync(articlesRequest).catch((err) => {
        console.error(err, action);
        setErrorLoadingArticles((p) => ({ ...p, [action]: true }));
        return manageError();
      });
    }

    return getArticlesAsync(articlesRequest).catch((err) => {
      console.error(err, action);
      setErrorLoadingArticles((p) => ({ ...p, [action]: true }));
      return manageError();
    });
  }

  useEffect(() => {
    if (!report_uuid && !isLoading) return;
    const loadArticles = async () => {
      const review = await loadArticlesByAction(ArticleActions.REVIEW, 1);
      const added = await loadArticlesByAction(ArticleActions.ADDED, 1);
      const removed = await loadArticlesByAction(ArticleActions.REMOVED, 1);

      return { REVIEW: review, ADDED: added, REMOVED: removed };
    };

    loadArticles().then((res) => {
      setArticlesHeap(res);
      setIsLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report_uuid]);

  function refreshArticlesByAction(
    action: ArticleActions,
    page: number,
    keep?: boolean,
    newPageSize?: number
  ) {
    loadArticlesByAction(action, page, newPageSize).then((articles) => {
      const currentArticles = articlesHeap[action];
      if (keep && currentArticles && currentArticles.items) {
        articles.items.push(...currentArticles.items, ...articles.items);
        // filter duplicates
        articles.items = articles.items
          .filter(
            (obj, index) =>
              articles.items.findIndex(
                (item) => item.item.external_id === obj.item.external_id
              ) === index
          )
          .sort(sortArticlesByScoreAndThenDate);
      }

      const newHeap = { ...articlesHeap, [action]: articles };
      setArticlesHeap(newHeap);
      setIsLoading(false);
    });
  }

  function appendItemToHeap(
    articleAction: ArticleActions,
    reviewArticles: ItemWithMetaData[]
  ) {
    if (!articlesHeap[articleAction]) return;
    let cleanedHeap = articlesHeap;
    // Remove article from other heaps
    Object.keys(articlesHeap).forEach((key) => {
      if (key !== articleAction) {
        const heap = articlesHeap[key as ArticleActions];
        if (!heap) return;

        const heapItems = heap.items
          .filter(
            (item) =>
              !reviewArticles.some(
                (p) => p.item.external_id === item.item.external_id
              )
          )
          .sort(sortArticlesByScoreAndThenDate);
        cleanedHeap = {
          ...cleanedHeap,
          [key]: { ...heap, items: heapItems },
        };
      }
    });

    // WARNING: We need to sort descending by created_at to be compliant with the backend sort
    // and then slice the array so we are the closes to the pagination state in the backend.
    let items = [
      ...reviewArticles,
      ...(articlesHeap[articleAction]?.items ?? []),
    ]
      .sort(sortArticlesByScoreAndThenDate)
      .slice(0, pageSize);

    const articles = {
      ...articlesHeap[articleAction],
      items,
      total: (articlesHeap[articleAction]?.total ?? 0) + 1,
      page: articlesHeap[articleAction]?.page ?? 1,
      size: pageSize,
    };

    cleanedHeap[articleAction] = articles;

    setArticlesHeap(cleanedHeap);
  }

  return {
    articlesHeap,
    isLoading,
    refreshArticlesByAction,
    appendItemToHeap,
    errorLoadingArticles,
    reviewPageSize,
    setReviewPageSize,
  };
}
